import { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';

/**
 * AdminNotificationsPage — `/admin/notifications`
 *
 * Controls instant + digest notification fan-out (SMS via Twilio,
 * email via Microsoft Graph). Per-user opt-in still applies — these are
 * site-wide caps + branding only.
 */

interface NotificationSettings {
  instantNotificationsEnabled?: boolean;
  subscriptionDigestHourUtc?: number;
  maxSmsInstantPerHour?: number;
  maxSmsDigestPerDay?: number;
  notificationsBrandName?: string;
  notificationsPublicBaseUrl?: string;
}

export default function AdminNotificationsPage() {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    void api.get<NotificationSettings>('/api/mgmt/settings').then(setSettings).catch(() => null);
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.patch('/api/mgmt/settings', settings);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <h1>Notifications</h1>

      {settings && (
        <section>
          <p className="text-muted">
            Site-wide controls for the subscription fan-out pipeline. Per-user opt-in
            is still required; these settings only cap volume and set the public
            branding used in SMS / email bodies.
          </p>

          <h2 className="h5 mt-4">Delivery</h2>
          <div className="form-check mb-2">
            <input
              id="instant-enabled"
              type="checkbox"
              className="form-check-input"
              checked={settings.instantNotificationsEnabled !== false}
              onChange={(e) => setSettings({ ...settings, instantNotificationsEnabled: e.target.checked })}
            />
            <label htmlFor="instant-enabled" className="form-check-label">
              Instant fan-out on event publish
            </label>
            <div className="form-text">
              When off, only the daily digest fires. Use this kill switch during incidents.
            </div>
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="digest-hour" className="form-label">Daily digest hour (UTC)</label>
              <input
                id="digest-hour"
                type="number"
                min={0}
                max={23}
                className="form-control"
                value={settings.subscriptionDigestHourUtc ?? 13}
                onChange={(e) => setSettings({ ...settings, subscriptionDigestHourUtc: parseInt(e.target.value, 10) })}
                aria-describedby="digest-hour-help"
              />
              <div id="digest-hour-help" className="form-text">
                Hour-of-day (UTC) the digest timer fires. 13 = 8 AM EST winter / 9 AM EDT summer.
              </div>
            </div>
          </div>

          <h2 className="h5 mt-4">SMS rate limits (per user)</h2>
          <p className="text-muted small">
            Soft caps — additional notifications are silently dropped once a user hits the cap.
            Twilio charges per message, so these directly bound cost in a spike.
          </p>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="sms-instant" className="form-label">Max instant SMS / user / hour</label>
              <input
                id="sms-instant"
                type="number"
                min={0}
                max={50}
                className="form-control"
                value={settings.maxSmsInstantPerHour ?? 5}
                onChange={(e) => setSettings({ ...settings, maxSmsInstantPerHour: parseInt(e.target.value, 10) })}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="sms-digest" className="form-label">Max digest SMS / user / day</label>
              <input
                id="sms-digest"
                type="number"
                min={0}
                max={10}
                className="form-control"
                value={settings.maxSmsDigestPerDay ?? 1}
                onChange={(e) => setSettings({ ...settings, maxSmsDigestPerDay: parseInt(e.target.value, 10) })}
              />
            </div>
          </div>

          <h2 className="h5 mt-4">Branding &amp; links</h2>
          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor="brand-name" className="form-label">Brand name</label>
              <input
                id="brand-name"
                type="text"
                maxLength={80}
                className="form-control"
                value={settings.notificationsBrandName ?? 'Murray Hill Events'}
                onChange={(e) => setSettings({ ...settings, notificationsBrandName: e.target.value })}
                aria-describedby="brand-name-help"
              />
              <div id="brand-name-help" className="form-text">
                Used in SMS / email subjects and footers. Max 80 chars.
              </div>
            </div>
            <div className="col-md-6">
              <label htmlFor="public-url" className="form-label">Public base URL</label>
              <input
                id="public-url"
                type="url"
                className="form-control"
                placeholder="https://events.jakegwynn.com"
                value={settings.notificationsPublicBaseUrl ?? ''}
                onChange={(e) => setSettings({ ...settings, notificationsPublicBaseUrl: e.target.value })}
                aria-describedby="public-url-help"
              />
              <div id="public-url-help" className="form-text">
                Used to build unsubscribe / manage / event-detail links. Must start with <code>http://</code> or <code>https://</code>; invalid values are rejected on save.
              </div>
            </div>
          </div>

          <button className="btn btn-primary mt-4" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {savedAt && (
            <span className="ms-3 text-success" role="status">
              ✓ Saved {new Date(savedAt).toLocaleTimeString()}
            </span>
          )}
        </section>
      )}
    </AdminLayout>
  );
}
